import {
  CreateDeliveryRequest,
  Delivery,
  UpdateDeliveryPositionRequest,
  UpdateDeliveryRequest,
} from "@repo/common"
import { DateUtils, DeliveryStatus, Route, populateRoute } from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

async function fetchDeliveriesByStatus(
  storeId: string,
  status?: DeliveryStatus,
): Promise<Delivery[]> {
  const url = populateRoute(
    Route.DELIVERY_CONTROLLER__FIND_ALL_BY_DELIVERY_STATUS,
    storeId,
  )
  return await request<Delivery[]>({
    url: status ? `${url}?status=${status}` : url,
    method: "GET",
  })
}

async function fetchDeliveriesBetweenDates(
  storeId: string,
  start: string,
  end: string,
): Promise<Delivery[]> {
  const url = populateRoute(
    Route.DELIVERY_CONTROLLER__FIND_BETWEEN_DATES,
    storeId,
  )
  return await request<Delivery[]>({
    url: `${url}?start=${start}&end=${end}`,
    method: "GET",
  })
}

async function fetchDeliveriesByJobId(jobId: string): Promise<Delivery[]> {
  const url = populateRoute(Route.DELIVERY_CONTROLLER__FIND_BY_JOB_ID, jobId)
  return await request<Delivery[]>({
    url,
    method: "GET",
  })
}

async function createDelivery(
  storeId: string,
  data: CreateDeliveryRequest,
): Promise<Delivery> {
  return await request<Delivery>({
    url: populateRoute(Route.DELIVERY_CONTROLLER__CREATE, storeId),
    method: "POST",
    data,
  })
}

async function updateDelivery(
  deliveryId: string,
  data: UpdateDeliveryRequest,
): Promise<Delivery> {
  return await request<Delivery>({
    url: populateRoute(Route.DELIVERY_CONTROLLER__EDIT, deliveryId),
    method: "PATCH",
    data,
  })
}

async function deleteDelivery(deliveryId: string): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.DELIVERY_CONTROLLER__DELETE, deliveryId),
    method: "DELETE",
  })
}

async function updateDeliveryPosition(
  storeId: string,
  data: UpdateDeliveryPositionRequest,
): Promise<Delivery> {
  return await request<Delivery>({
    url: populateRoute(Route.DELIVERY_CONTROLLER__UPDATE_POSITION, storeId),
    method: "PATCH",
    data,
  })
}

export const deliveryQueries = {
  all: (storeId: string) => ["deliveries", storeId],
  byStatus: (storeId: string, status: DeliveryStatus) => [
    ...deliveryQueries.all(storeId),
    "status",
    status,
  ],
  byJobId: (jobId: string) =>
    queryOptions({
      queryKey: ["deliveries", "job", jobId],
      queryFn: () => fetchDeliveriesByJobId(jobId),
    }),
  betweenDates: (storeId: string, start: Date, end: Date) => {
    const startDateKey = DateUtils.getDateKey(DateUtils.getStartOfDay(start))
    const endDateKey = DateUtils.getDateKey(DateUtils.getEndOfDay(end))
    return queryOptions({
      queryKey: [
        "deliveries",
        storeId,
        "betweenDates",
        startDateKey,
        endDateKey,
      ],
      queryFn: () =>
        fetchDeliveriesBetweenDates(storeId, startDateKey, endDateKey),
    })
  },
  todo: (storeId: string) =>
    queryOptions({
      queryKey: deliveryQueries.byStatus(storeId, DeliveryStatus.TODO),
      queryFn: () => fetchDeliveriesByStatus(storeId, DeliveryStatus.TODO),
    }),
  waiting: (storeId: string) =>
    queryOptions({
      queryKey: deliveryQueries.byStatus(storeId, DeliveryStatus.WAITING),
      queryFn: () => fetchDeliveriesByStatus(storeId, DeliveryStatus.WAITING),
    }),
  delivered: (storeId: string) =>
    queryOptions({
      queryKey: deliveryQueries.byStatus(storeId, DeliveryStatus.DELIVERED),
      queryFn: () => fetchDeliveriesByStatus(storeId, DeliveryStatus.DELIVERED),
    }),
}

export function useCreateDelivery(storeId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateDeliveryRequest) => createDelivery(storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliveries", storeId] })
      queryClient.invalidateQueries({ queryKey: ["deliveries", "job"] })
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
    },
  })
}

export function useUpdateDelivery(storeId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      deliveryId,
      data,
    }: {
      deliveryId: string
      data: UpdateDeliveryRequest
    }) => updateDelivery(deliveryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliveries", storeId] })
      queryClient.invalidateQueries({ queryKey: ["deliveries", "job"] })
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
    },
  })
}

export function useUpdateDeliveryPosition(storeId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateDeliveryPositionRequest) =>
      updateDeliveryPosition(storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliveries", storeId] })
      queryClient.invalidateQueries({ queryKey: ["deliveries", "job"] })
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
    },
  })
}

export function useDeleteDelivery(storeId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (deliveryId: string) => deleteDelivery(deliveryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliveries", storeId] })
      queryClient.invalidateQueries({ queryKey: ["deliveries", "job"] })
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
    },
  })
}
